/**
 * Generic Ticket Preview Utility
 * 
 * Provides a consistent interface for generating ticket preview HTML
 * across all POS components (CheckoutDialog, RepairTicketDialog, TicketDetailPage, etc.)
 */

import { getThermalReceiptTemplate } from '../components/ThermalReceiptTemplate';

/**
 * Generate ticket preview HTML based on ticket type and data
 * 
 * @param {Object} options - Preview configuration
 * @param {Array} options.cart - Line items (with Quantity, Price, PriceInclVAT, etc.)
 * @param {Object} options.customer - Customer object or null
 * @param {Object} options.storeInfo - Store information
 * @param {string} options.ticketType - 'Sale' or 'Repair'
 * @param {string} options.receiptNumber - Ticket number
 * @param {number} options.paidAmount - Amount paid (null or 0 if unpaid)
 * @param {Object} options.paymentMethod - Selected payment method object (with id and name)
 * @param {Array} options.allPaymentMethods - All available payment methods for lookup
 * @param {boolean} options.useMultiplePayments - Whether using multiple payment methods
 * @param {Array} options.paymentMethodsArray - Array of payment method objects for multi-payment
 * @param {Object} options.discount - Discount object with amount
 * @param {number} options.customVATRate - Custom VAT rate override
 * @param {string} options.notes - Footer notes for the ticket
 * @param {string} options.walkInCustomerName - Name for walk-in customer when no customer selected
 * @param {Date} options.pickupDate - Pickup date for repair tickets
 * @param {boolean} options.isTvaIncluded - Whether prices include VAT
 * 
 * @returns {string} HTML string for ticket preview
 */
export const generateTicketPreview = ({
  cart = [],
  customer = null,
  storeInfo = null,
  ticketType = 'Sale',
  receiptNumber = '',
  paidAmount = 0,
  paymentMethod = null,
  allPaymentMethods = [],
  useMultiplePayments = false,
  paymentMethodsArray = [],
  discount = null,
  customVATRate = null,
  notes = '*** THANK YOU FOR YOUR VISIT ***',
  walkInCustomerName = '',
  pickupDate = null,
  isTvaIncluded = false,
  preCalculatedTotals = null, // Optional pre-calculated totals from database
}) => {
  try {
    // Validate required parameters
    if (!storeInfo) {
      return getEmptyPreviewTemplate('Store information not available');
    }

    if (!cart || cart.length === 0) {
      return getEmptyPreviewTemplate('Add products to see preview');
    }

    // Format payment method name
    const paymentMethodName = paymentMethod?.name || paymentMethod?.Name || null;

    // Generate preview using the template
    return getThermalReceiptTemplate(
      cart,
      customer,
      storeInfo,
      paymentMethodName,
      paidAmount,
      receiptNumber,
      discount,
      customVATRate,
      notes,
      useMultiplePayments,
      paymentMethodsArray,
      allPaymentMethods,
      walkInCustomerName,
      ticketType,
      pickupDate,
      isTvaIncluded,
      preCalculatedTotals // Pass through pre-calculated totals
    );
  } catch (error) {
    console.error('Ticket preview error:', error);
    return getErrorPreviewTemplate(error.message);
  }
};

/**
 * Generate preview for a Sale ticket (simplified interface)
 */
export const generateSaleTicketPreview = ({
  cart,
  customer,
  storeInfo,
  receiptNumber,
  paidAmount = 0,
  paymentMethod = null,
  allPaymentMethods = [],
  useMultiplePayments = false,
  paymentMethodsArray = [],
  discount = null,
  customVATRate = null,
  notes = '*** THANK YOU FOR YOUR VISIT ***',
  walkInCustomerName = '',
  isTvaIncluded = false,
  preCalculatedTotals = null,
}) => {
  return generateTicketPreview({
    cart,
    customer,
    storeInfo,
    ticketType: 'Sale',
    receiptNumber,
    paidAmount,
    paymentMethod,
    allPaymentMethods,
    useMultiplePayments,
    paymentMethodsArray,
    discount,
    customVATRate,
    notes,
    walkInCustomerName,
    pickupDate: null,
    isTvaIncluded,
    preCalculatedTotals,
  });
};

/**
 * Generate preview for a Repair ticket (simplified interface)
 */
export const generateRepairTicketPreview = ({
  cart,
  customer,
  customerName = '',
  customerPhone = '',
  storeInfo,
  receiptNumber,
  paidAmount = 0,
  paymentMethod = null,
  allPaymentMethods = [],
  discount = null,
  customVATRate = null,
  notes = '',
  pickupDate = null,
  isTvaIncluded = false,
  preCalculatedTotals = null,
}) => {
  // Build customer object if we have name/phone but no customer entity
  const customerData = customer || (customerName ? {
    Name: customerName,
    PhoneNumber: customerPhone || ''
  } : null);

  return generateTicketPreview({
    cart,
    customer: customerData,
    storeInfo,
    ticketType: 'Repair',
    receiptNumber,
    paidAmount,
    paymentMethod,
    allPaymentMethods,
    useMultiplePayments: false, // Repair tickets don't use multiple payments
    paymentMethodsArray: [],
    discount,
    customVATRate,
    notes,
    walkInCustomerName: '', // Not used for repair tickets since customer is required
    pickupDate,
    isTvaIncluded,
    preCalculatedTotals,
  });
};

/**
 * Get empty preview template
 */
const getEmptyPreviewTemplate = (message = 'Add products to see preview') => {
  return `
    <html>
      <body style="font-family:sans-serif;padding:40px 20px;text-align:center;color:#9e9e9e">
        <p style="font-size:14px">${message}</p>
      </body>
    </html>
  `;
};

/**
 * Get error preview template
 */
const getErrorPreviewTemplate = (errorMessage) => {
  return `
    <html>
      <body style="padding:20px;color:#f44336;font-family:sans-serif">
        Preview error: ${errorMessage}
      </body>
    </html>
  `;
};
